import salaryService from 'ApiService/Accountant/salaryService';
import InputField from 'components/fields/InputField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const EmployeeSalaryDetails = ({ employeeData }) => {
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [monthlySalaryData, setMonthlySalaryData] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    amount: '0',
    bonus: '0',
    deductions: '0',
    notes: '',
  });
  const [employeeSalaryDetails, setEmployeeSalaryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disable, setDisable] = useState(true);

  const { state } = useContext(AuthContext);

  useEffect(() => {
    if (employeeData) {
      fetchEmployeeSalaryDetails();
    }
  }, [employeeData]);

  const fetchEmployeeSalaryDetails = async () => {
    try {
      setLoading(true);
      const selectedBranch = state?.selectedBranch || '';

      const response = await salaryService.getEmployeeSalaryDetails({
        employeeId: employeeData.employeeId,
        year: new Date().getFullYear(),
        selectedBranch,
      });
      if (response.data.success) {
        setEmployeeSalaryDetails(response.data.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const handleMonthlySalaryChange = (e) => {
    const { name, value } = e.target;

    if (['month', 'amount', 'bonus', 'deductions'].includes(name)) {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    setMonthlySalaryData({
      ...monthlySalaryData,
      [name]: value,
    });
  };
  const handleSubmitClick = () => {
    setShowConfirmation(true);
  };
  const handleSubmit = async () => {
    if (loading) return;
    try {
      setLoading(true);
      if (
        !employeeData.employeeId ||
        !employeeData.employeeType ||
        !monthlySalaryData.year ||
        !monthlySalaryData.month ||
        monthlySalaryData.amount === '' ||
        monthlySalaryData.bonus === '' ||
        monthlySalaryData.deductions === ''
      ) {
        toast.error('All fields are required.');
        return;
      }
      const selectedBranch = state?.selectedBranch || '';
      if (
        isNaN(monthlySalaryData.amount) ||
        monthlySalaryData.amount <= 0 ||
        isNaN(monthlySalaryData.bonus) ||
        monthlySalaryData.bonus < 0 ||
        isNaN(monthlySalaryData.deductions) ||
        monthlySalaryData.deductions < 0
      ) {
        toast.error(
          'Amount, bonus, and deductions must be valid positive numbers. Amount should be greater than zero.'
        );
        return;
      }
      const payload = {
        selectedBranch,
        employeeId: employeeData.employeeId,
        employeeType: employeeData.employeeType,
        ...monthlySalaryData,
        amount: parseFloat(monthlySalaryData.amount),
        bonus: parseFloat(monthlySalaryData.bonus) || 0,
        deductions: parseFloat(monthlySalaryData.deductions) || 0,
      };

      const response = await salaryService.addMonthlySalary(payload);
      if (response.data.success) {
        fetchEmployeeSalaryDetails();
        toast.success(response?.data?.message);
        setShowConfirmation(false);
        setMonthlySalaryData({
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          amount: '',
          bonus: '',
          deductions: '',
          notes: '',
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months[monthNumber - 1];
  };

  const getConfirmationMessage = () => {
    const totalAmount = (
      parseFloat(monthlySalaryData.amount) +
      parseFloat(monthlySalaryData.bonus || 0) -
      parseFloat(monthlySalaryData.deductions || 0)
    ).toFixed(2);

    return (
      <div className="space-y-2">
        <p>Please confirm the following salary details:</p>
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>
            Month: {getMonthName(monthlySalaryData.month)}{' '}
            {monthlySalaryData.year}
          </li>
          <li>
            Base Amount: ₹
            {parseFloat(monthlySalaryData.amount).toLocaleString()}
          </li>
          <li>
            Bonus: ₹{parseFloat(monthlySalaryData.bonus || 0).toLocaleString()}
          </li>
          <li>
            Deductions: ₹
            {parseFloat(monthlySalaryData.deductions || 0).toLocaleString()}
          </li>
          <li className="font-semibold">
            Total Amount: ₹{parseFloat(totalAmount).toLocaleString()}
          </li>
        </ul>
      </div>
    );
  };

  const handleNavigation = () => {
    if (state?.user?.role === 'superadmin') {
      navigate('/superadmin/create/salary');
    } else if (state?.user?.role === 'admin') {
      navigate('/admin/create/salary');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-navy-900 dark:text-white">
          Employee Salary Details
        </h1>

        {!employeeSalaryDetails ? (
          state?.user?.role === 'account' ? (
            <p className="text-sm font-medium text-gray-700">
              Please contact the admin to manage salary details.
            </p>
          ) : (
            <button
              onClick={handleNavigation}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
            >
              Create Salary Details
            </button>
          )
        ) : null}
      </div>

      {employeeSalaryDetails ? (
        <div className="mb-8 grid gap-6 lg:grid-cols-2">
          {/* Salary Overview Card */}
          <div className="rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
            <div className="border-b pb-4">
              <p className="text-sm text-navy-700 dark:text-navy-100">
                Annual Salary
              </p>
              <p className="text-2xl font-bold text-navy-900 dark:text-white">
                ₹{employeeSalaryDetails?.currentSalary?.toLocaleString()}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-navy-700 dark:text-navy-100">
                  Monthly
                </p>
                <p className="text-lg font-semibold text-navy-900 dark:text-white">
                  ₹{(employeeSalaryDetails?.currentSalary / 12).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-navy-700 dark:text-navy-100">
                  Daily
                </p>
                <p className="text-lg font-semibold text-navy-900 dark:text-white">
                  ₹{(employeeSalaryDetails?.currentSalary / 365).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Employee Info Card */}
          <div className="rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
            <div className="border-b pb-4">
              <p className="text-sm text-navy-700 dark:text-navy-100">
                Employee Information
              </p>
              <p className="text-lg font-semibold text-navy-900 dark:text-white">
                {employeeSalaryDetails?.employeeId?.name}
              </p>
            </div>
            <div className="mt-4 space-y-2">
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Employee Type
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.employeeType === 'Account'
                    ? 'Accountant'
                    : employeeSalaryDetails?.employeeType === 'Placement'
                    ? 'Placement Officer'
                    : employeeSalaryDetails?.employeeType}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Account Number
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.bankDetails?.accountNumber}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-navy-700 dark:text-navy-100">
                  Bank Name
                </p>
                <p className="text-navy-900 dark:text-navy-300">
                  {employeeSalaryDetails?.bankDetails?.bankName}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-white p-6 text-center shadow-sm dark:bg-navy-800">
          <p className="text-navy-700 dark:text-navy-100">
            No salary details found for this employee.
          </p>
        </div>
      )}

      {/* Add Monthly Salary Form */}
      <div className="rounded-xl bg-white p-6 shadow-sm dark:bg-navy-800">
        <h2 className="mb-6 text-lg font-semibold text-navy-900 dark:text-white">
          Add Monthly Salary
        </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <InputField
            label="Year"
            name="year"
            type="text"
            value={monthlySalaryData.year}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <InputField
            label="Month"
            name="month"
            type="text"
            maxLength={2}
            value={monthlySalaryData.month}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <InputField
            label="Amount"
            name="amount"
            type="text"
            value={monthlySalaryData.amount}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <InputField
            label="Bonus"
            name="bonus"
            type="text"
            value={monthlySalaryData.bonus}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <InputField
            label="Deductions"
            name="deductions"
            type="text"
            value={monthlySalaryData.deductions}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <InputField
            label="Notes"
            name="notes"
            type="text"
            value={monthlySalaryData.notes}
            onChange={handleMonthlySalaryChange}
            className="w-full rounded-lg border p-2.5 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <button
          onClick={handleSubmitClick}
          className="mt-6 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
        >
          {loading ? 'Adding...' : ' Add Monthly Salary'}
        </button>
      </div>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => {
          if (!loading) setShowConfirmation(false);
        }}
        onConfirm={handleSubmit}
        title="Confirm Monthly Salary"
        message={getConfirmationMessage()}
        confirmText={loading ? 'Processing...' : 'Yes, Add'}
        cancelText="Cancel"
        icon="info"
        disableConfirm={loading}
      />
    </div>
  );
};

export default EmployeeSalaryDetails;
